/**
 * @file backdrop-images.ts
 * @description Рисунки фона числами (phase19.16, партия SB120, этап 2;
 * SB11, SB120; проект uiux-sb120-stage2 §2 «Фон и рамка», §5; данные
 * db-design-sb120-stage2 §1 `backdrop.images[] {x, y, size, sizeTablet?,
 * sizePhone?, opacity?, hideOnMobile}`). Тема и фон страницы
 * (`SitePage.background.decor`) — одна схема и одни функции.
 *
 * Чистые функции: новый рисунок (угол → точка, прежний размер M), правка
 * необязательного ключа без «undefined» в документе (иначе документ после
 * «открыть и сохранить» отличался бы от прочитанного), предупреждение
 * «закроет текст на телефоне».
 */

import {
  BACKDROP_CORNER_V1,
  backdropImageWidth,
  BACKDROP_SIZE_V1,
  SITE_BACKDROP_BREAKPOINTS,
  type SiteBackdropCornerV1,
  type SiteBackdropImage,
} from "@chaiberi/shared";

/** Необязательные ключи рисунка: нет ключа — «по доле» / «как было». */
export type BackdropOptionalKey = "sizeTablet" | "sizePhone" | "opacity";

/** Новый рисунок: место — прежний угол, размер — прежний M (320 / по доле), на телефоне скрыт. */
export function newBackdropImage(mediaId: string, corner: SiteBackdropCornerV1): SiteBackdropImage {
  const point = BACKDROP_CORNER_V1[corner];
  return { mediaId, x: point.x, y: point.y, size: BACKDROP_SIZE_V1.m.size, hideOnMobile: true };
}

/** Рисунок с новым значением необязательного ключа; `undefined` — ключ снимается. */
export function withOptional(image: SiteBackdropImage, key: BackdropOptionalKey, value: number | undefined): SiteBackdropImage {
  const next = { ...image };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next;
}

/** Ширина экрана телефона для предупреждения (самый узкий из проверяемых). */
const PHONE_WIDTH = 360;
/** Доля ширины телефона, после которой рисунок закрывает текст (§5). */
const PHONE_SHARE_MAX = 0.4;

/** Рисунок виден на телефоне и шире 40 % экрана — закроет текст. */
export function backdropTooWideOnPhone(image: SiteBackdropImage): boolean {
  return !image.hideOnMobile && backdropImageWidth(image, "phone") > PHONE_WIDTH * PHONE_SHARE_MAX;
}

/** «на планшете 220 · на телефоне 140 px» — под размером ПК. */
export function backdropDeviceSummary(image: SiteBackdropImage): string {
  return `на планшете ${backdropImageWidth(image, "tablet")} · на телефоне ${backdropImageWidth(image, "phone")} px`;
}

/** «ПК» у рисунков — от 1024 px (не 1280, как у секций). */
export const BACKDROP_DESKTOP_FROM = SITE_BACKDROP_BREAKPOINTS.desktop;
